<template>
  <section class="duallo-admin-table">
    <header class="duallo-table-header">
      <div>
        <div class="duallo-eyebrow">Organization</div>
        <h3 class="duallo-table-title">Manage Subscription</h3>
      </div>
      <div class="table-controls">
        <div class="year-picker">
          <v-select v-model="selectedYear" :items="availableYears" variant="outlined" density="compact" hide-details
            prepend-inner-icon="mdi-calendar-blank-outline" @update:model-value="onYearChanged" />
        </div>
      </div>
    </header>

    <div class="duallo-table-card">
      <div v-if="isLoading" class="duallo-state-block">
        <div v-for="n in 3" :key="n" class="duallo-row-skeleton" />
      </div>

      <div v-else-if="error" class="duallo-state-block empty-state">
        <v-icon size="40" color="#ef4444">mdi-alert-circle-outline</v-icon>
        <p class="empty-title">Couldn't load subscriptions</p>
        <p class="empty-subtitle">{{ error }}</p>
      </div>

      <div v-else-if="!subscriptions.length" class="duallo-state-block empty-state">
        <v-icon size="40" color="#4a5a60">mdi-package-variant-closed</v-icon>
        <p class="empty-title">No subscriptions on file</p>
        <p class="empty-subtitle">This organization doesn't have a subscription history yet.</p>
      </div>

      <div v-else-if="filteredSubscriptions.length === 0" class="duallo-state-block empty-state">
        <v-icon size="40" color="#4a5a60">mdi-calendar-blank-outline</v-icon>
        <p class="empty-title">No subscriptions in {{ selectedYear }}</p>
        <p class="empty-subtitle">Pick a different year to view its subscription periods.</p>
      </div>

      <template v-else>
        <div class="period-list">
          <div
            v-for="(sub, idx) in filteredSubscriptions"
            :key="sub.subscriptionPeriod"
            class="period"
            :class="{ 'is-expanded': expanded === sub.subscriptionPeriod, 'is-divided': idx > 0 }"
          >
            <button class="period-header" type="button" @click="toggle(sub.subscriptionPeriod)">
              <v-icon size="16" class="caret">
                {{ expanded === sub.subscriptionPeriod ? 'mdi-chevron-down' : 'mdi-chevron-right' }}
              </v-icon>

              <div class="period-label">
                <div class="period-month">{{ formatPeriod(sub.subscriptionPeriod) }}</div>
                <div class="period-plan">{{ sub.name || 'No plan' }}</div>
              </div>

              <div class="period-usage">
                <span class="usage-numbers">
                  <span class="used">{{ formatNumber(sub.usedMinutes) }}</span>
                  <span class="of"> / {{ formatNumber(sub.monthlyMinutes) }} mins</span>
                </span>
                <v-progress-linear
                  :model-value="progress(sub)"
                  :color="usageColor(progress(sub))"
                  height="5"
                  rounded
                  bg-color="#101820"
                  class="usage-bar"
                />
                <span class="usage-pct">{{ progress(sub) }}%</span>
              </div>

              <span class="period-receipts-count">{{ receiptCountLabel(sub.subscriptionPeriod) }}</span>

              <span class="status-badge" :class="statusBadgeClass(sub.status)">{{ sub.status || '—' }}</span>
            </button>

            <div v-if="expanded === sub.subscriptionPeriod" class="period-body">
              <div v-if="loadingPeriod === sub.subscriptionPeriod" class="duallo-state-block inner">
                <div v-for="n in 3" :key="n" class="duallo-row-skeleton" />
              </div>

              <div v-else-if="(receiptsByPeriod[sub.subscriptionPeriod] || []).length === 0" class="duallo-state-block empty-state inner">
                <v-icon size="32" color="#4a5a60">mdi-receipt-text-remove-outline</v-icon>
                <p class="empty-title">No scan receipts</p>
                <p class="empty-subtitle">Nothing was billed against this subscription period.</p>
              </div>

              <div v-else-if="!isCompact" class="duallo-table-wrap inner-table">
                <table class="duallo-data-table">
                  <thead>
                    <tr>
                      <th class="col-icon"></th>
                      <th class="col-scan">Scan</th>
                      <th class="col-duration">Duration</th>
                      <th class="col-charged">Charged</th>
                      <th class="col-when">When</th>
                      <th class="col-status">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="r in receiptsByPeriod[sub.subscriptionPeriod]" :key="r.id">
                      <td class="cell-icon"><v-icon size="18" :color="iconColor(r)">{{ r.fileTypeIcon }}</v-icon></td>
                      <td class="cell-scan">
                        <div class="cell-primary" :title="r.displayName">{{ r.displayName }}</div>
                        <div v-if="r.sourceFileName && r.sourceFileName !== r.displayName" class="cell-secondary" :title="r.sourceFileName">
                          {{ r.sourceFileName }}
                        </div>
                      </td>
                      <td class="cell-numeric">{{ r.durationLabel }}</td>
                      <td class="cell-numeric">{{ formatNumber(r.minutesCharged) }} min</td>
                      <td class="cell-when">{{ formatDateTime(r.createdOn) }}</td>
                      <td class="cell-status">
                        <span class="status-badge small" :class="receiptStatusClass(r.status)">{{ r.status || '—' }}</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div v-else class="receipt-card-list">
                <article v-for="r in receiptsByPeriod[sub.subscriptionPeriod]" :key="r.id" class="receipt-card">
                  <div class="receipt-card-head">
                    <v-icon size="18" :color="iconColor(r)">{{ r.fileTypeIcon }}</v-icon>
                    <div class="receipt-card-title">
                      <div class="cell-primary" :title="r.displayName">{{ r.displayName }}</div>
                      <div v-if="r.sourceFileName && r.sourceFileName !== r.displayName" class="cell-secondary" :title="r.sourceFileName">
                        {{ r.sourceFileName }}
                      </div>
                    </div>
                    <span class="status-badge small" :class="receiptStatusClass(r.status)">{{ r.status || '—' }}</span>
                  </div>
                  <dl class="receipt-card-fields">
                    <div class="receipt-field">
                      <dt>Duration</dt>
                      <dd class="cell-numeric">{{ r.durationLabel }}</dd>
                    </div>
                    <div class="receipt-field">
                      <dt>Charged</dt>
                      <dd class="cell-numeric">{{ formatNumber(r.minutesCharged) }} min</dd>
                    </div>
                    <div class="receipt-field">
                      <dt>When</dt>
                      <dd>{{ formatDateTime(r.createdOn) }}</dd>
                    </div>
                  </dl>
                </article>
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>
  </section>
</template>

<script setup>
import { ref, computed, onMounted, defineOptions } from 'vue';
import { useDisplay } from 'vuetify';
import OrganizationService from '@/services/OrganizationService';
import { useOrganizationStore } from '@/store/organizationStore';

defineOptions({ name: 'ManageSubscriptionPanel' });

const organizationStore = useOrganizationStore();

const { smAndDown } = useDisplay();
const isCompact = computed(() => smAndDown.value);

const isLoading = ref(true);
const error = ref(null);
const subscriptions = ref([]);
const expanded = ref(null);
const loadingPeriod = ref(null);
const receiptsByPeriod = ref({});
const selectedYear = ref(new Date().getFullYear());

const sortedSubscriptions = computed(() =>
  // YYYY-MM keys sort lexicographically the same as chronologically; newest first.
  [...subscriptions.value].sort((a, b) => (b.subscriptionPeriod || '').localeCompare(a.subscriptionPeriod || ''))
);

const availableYears = computed(() => {
  // Always include the current year so the default selection is valid even if
  // the org hasn't had any subscription activity yet this year.
  const years = new Set([new Date().getFullYear()]);
  for (const s of subscriptions.value) {
    const yr = parseYear(s.subscriptionPeriod);
    if (yr) years.add(yr);
  }
  return [...years].sort((a, b) => b - a);  // newest first
});

const filteredSubscriptions = computed(() =>
  sortedSubscriptions.value.filter(s => parseYear(s.subscriptionPeriod) === selectedYear.value)
);

function parseYear(periodKey) {
  if (!periodKey) return null;
  const yr = Number(periodKey.split('-')[0]);
  return Number.isFinite(yr) ? yr : null;
}

onMounted(async () => {
  await loadSubscriptions();
});

async function loadSubscriptions() {
  isLoading.value = true;
  error.value = null;
  try {
    subscriptions.value = await OrganizationService.getSubscriptionsByOrganizationId(organizationStore.organizationId);
    // Auto-expand the newest period IN THE SELECTED YEAR for context; defer
    // the receipts fetch to keep initial mount lean. If the current year has
    // no periods we leave nothing expanded — the empty state takes over.
    await autoExpandLatestInYear();
  } catch (e) {
    console.error('Failed to load subscriptions', e);
    error.value = 'Could not load subscriptions for this organization.';
  } finally {
    isLoading.value = false;
  }
}

async function autoExpandLatestInYear() {
  const first = filteredSubscriptions.value[0]?.subscriptionPeriod ?? null;
  expanded.value = first;
  if (first && !receiptsByPeriod.value[first]) {
    await loadReceipts(first);
  }
}

async function onYearChanged() {
  await autoExpandLatestInYear();
}

async function toggle(periodKey) {
  if (expanded.value === periodKey) {
    expanded.value = null;
    return;
  }
  expanded.value = periodKey;
  if (!receiptsByPeriod.value[periodKey]) {
    await loadReceipts(periodKey);
  }
}

async function loadReceipts(periodKey) {
  loadingPeriod.value = periodKey;
  try {
    const records = await OrganizationService.getSubscriptionUsageRecords(
      organizationStore.organizationId,
      periodKey
    );
    receiptsByPeriod.value = { ...receiptsByPeriod.value, [periodKey]: records };
  } catch (e) {
    console.error(`Failed to load receipts for period ${periodKey}`, e);
    receiptsByPeriod.value = { ...receiptsByPeriod.value, [periodKey]: [] };
  } finally {
    loadingPeriod.value = null;
  }
}

function receiptCountLabel(periodKey) {
  const arr = receiptsByPeriod.value[periodKey];
  if (!arr) return '';
  if (arr.length === 0) return '0 scans';
  return arr.length === 1 ? '1 scan' : `${arr.length} scans`;
}

function progress(sub) {
  if (!sub.monthlyMinutes) return 0;
  const pct = ((sub.usedMinutes ?? 0) / sub.monthlyMinutes) * 100;
  return Math.min(Math.round(pct), 100);
}

function usageColor(pct) {
  if (pct >= 90) return '#ef4444';
  if (pct >= 70) return '#f59e0b';
  return '#3b82f6';
}

function statusBadgeClass(status) {
  const s = (status || '').toLowerCase();
  if (s === 'active') return 'badge-active';
  return 'badge-neutral';
}

function receiptStatusClass(status) {
  const s = (status || '').toLowerCase();
  if (s === 'charged') return 'badge-charged';
  if (s === 'refunded') return 'badge-refunded';
  return 'badge-neutral';
}

function iconColor(record) {
  return (record.status || '').toLowerCase() === 'refunded' ? '#8aa0aa' : '#cfd8dc';
}

function formatNumber(n) {
  if (n == null) return '0';
  return Number(n).toLocaleString();
}

function formatPeriod(periodKey) {
  if (!periodKey) return '—';
  const [year, month] = periodKey.split('-').map(Number);
  if (!year || !month) return periodKey;
  return new Date(Date.UTC(year, month - 1, 1)).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    timeZone: 'UTC',
  });
}

function formatDateTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
</script>

<style scoped>
/* Shell (.duallo-admin-table / -table-header / -eyebrow / -table-title), loading/empty
   container (.duallo-state-block), skeleton, and table wrap + base come from the shared
   duallo-components.css. Kept below: this panel's card surface, controls, empty-state
   text, the bespoke period list, status badges, receipt cells, and per-file overrides. */

.table-controls {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.year-picker {
  min-width: 140px;
}

.year-picker :deep(.v-field) {
  font-variant-numeric: tabular-nums;
}


.duallo-state-block.inner { padding: 14px; }

.empty-state {
  align-items: center;
  text-align: center;
  padding: 40px 16px;
  color: #8aa0aa;
}

.empty-state.inner { padding: 26px 16px; }

.empty-title {
  font-size: 1.05rem;
  font-weight: 600;
  color: #cfd8dc;
  margin: 12px 0 4px;
}

.empty-subtitle {
  font-size: 0.88rem;
  margin: 0;
  max-width: 380px;
}

/* Shared cells are 0.92rem; receipts use a slightly smaller size. */
.duallo-data-table td { font-size: 0.88rem; }

/* Shared row hover is teal; this receipts table uses a subtle neutral hover. */
.duallo-data-table tbody tr:hover { background: rgba(255, 255, 255, 0.02); }

/* ── period list ── */
.period-list {
  display: flex;
  flex-direction: column;
}

.period.is-divided {
  border-top: 1px solid rgba(255, 255, 255, 0.05);
}

.period.is-expanded {
  background: rgba(255, 255, 255, 0.015);
}

.period-header {
  width: 100%;
  display: grid;
  grid-template-columns: 18px minmax(170px, 1.3fr) minmax(260px, 1.8fr) auto auto;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  background: transparent;
  border: none;
  color: inherit;
  cursor: pointer;
  text-align: left;
  transition: background-color 120ms ease;
}

.period-header:hover { background: rgba(255, 255, 255, 0.025); }

.caret { color: #8aa0aa; }

.period-label { min-width: 0; }

.period-month {
  font-weight: 700;
  font-size: 0.95rem;
  color: #ffffff;
}

.period-plan {
  font-size: 0.78rem;
  color: #8aa0aa;
}

.period-usage {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  font-size: 0.78rem;
  color: #8aa0aa;
}

.usage-numbers { font-variant-numeric: tabular-nums; white-space: nowrap; }
.usage-numbers .used { color: #ffffff; font-weight: 600; }
.usage-bar { flex: 1 1 auto; min-width: 60px; }
.usage-pct {
  font-weight: 700;
  color: #ffffff;
  font-variant-numeric: tabular-nums;
  text-align: right;
  min-width: 36px;
}

.period-receipts-count {
  font-size: 0.78rem;
  color: #8aa0aa;
  white-space: nowrap;
  min-width: 62px;
  text-align: right;
}

.period-body {
  padding: 0 16px 16px 44px;
  background: rgba(0, 0, 0, 0.18);
}

.inner-table {
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.04);
  background: rgba(0, 0, 0, 0.18);
  overflow: hidden;
}

.inner-table .duallo-data-table thead th {
  background: rgba(0, 0, 0, 0.32);
}

/* ── status badges ── */
.status-badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 9px;
  font-size: 0.68rem;
  font-weight: 600;
  border-radius: 999px;
  text-transform: capitalize;
  white-space: nowrap;
}

.status-badge.small {
  padding: 1px 8px;
  font-size: 0.65rem;
}

.badge-active {
  background-color: rgba(34, 197, 94, 0.12);
  border: 1px solid rgba(34, 197, 94, 0.3);
  color: #4ade80;
}

.badge-charged {
  background-color: rgba(59, 130, 246, 0.12);
  border: 1px solid rgba(59, 130, 246, 0.3);
  color: #60a5fa;
}

.badge-refunded {
  background-color: rgba(239, 68, 68, 0.10);
  border: 1px solid rgba(239, 68, 68, 0.3);
  color: #f87171;
}

.badge-neutral {
  background-color: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: #cfd8dc;
}

/* ── receipt table cells ── */
.col-icon { width: 36px; }
.col-duration, .col-charged { width: 110px; }
.col-when { width: 150px; }
.col-status { width: 110px; }

.cell-icon { padding-right: 4px; }

.cell-scan {
  max-width: 360px;
  min-width: 180px;
}

.cell-primary {
  font-weight: 600;
  color: #ffffff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.cell-secondary {
  font-size: 0.72rem;
  color: #8aa0aa;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-top: 2px;
}

.cell-numeric {
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.cell-when { white-space: nowrap; }

/* ── mobile receipt cards (smAndDown) ── */
.receipt-card-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px;
}

.receipt-card {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px 14px;
  background: rgba(0, 0, 0, 0.18);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 10px;
}

.receipt-card-head {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.receipt-card-title { flex: 1 1 auto; min-width: 0; }

/* Card titles wrap instead of the table's single-line ellipsis. */
.receipt-card-title .cell-primary,
.receipt-card-title .cell-secondary {
  white-space: normal;
  overflow: visible;
  text-overflow: clip;
  word-break: break-word;
}

.receipt-card-fields {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 0;
}

.receipt-field {
  display: grid;
  grid-template-columns: 84px 1fr;
  gap: 10px;
  align-items: baseline;
}

.receipt-field dt {
  font-size: 0.68rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #8aa0aa;
  font-weight: 700;
}

.receipt-field dd {
  margin: 0;
  min-width: 0;
  font-size: 0.85rem;
  color: #cfd8dc;
}

/* ── responsive ── */
@media (max-width: 960px) {
  .period-header {
    grid-template-columns: 18px minmax(170px, 1fr) auto auto;
  }
  .period-usage {
    grid-column: 1 / -1;
    padding-left: 32px;
  }
  .period-body {
    padding-left: 16px;
  }
  .duallo-data-table td { font-size: 0.82rem; padding: 10px 12px; }
  .duallo-data-table thead th { padding: 12px; }
}
</style>
